import Section from "../Components/Section";
import PageHeader from "../Components/PageHeader";
import { aboutUsData } from "../Data/Data";
import useChangeTitle from "../Hooks/useChangeTitle.jsx";
import { Link } from "react-router-dom";
import Button from "../Components/Button";

function About() {
  useChangeTitle("About Us");

  return (
    <>
      <PageHeader heading="About Us" />

      {/* Hero Section with Background */}
      <div className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative z-10 py-24 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-8 animate-fade-in">
              Engineering Excellence Since Day One
            </h1>
            <p className="text-xl mb-12 max-w-3xl mx-auto leading-relaxed">
              Rakan EG stands as a beacon of innovation in Egypt's electromechanical industry, 
              delivering cutting-edge solutions that shape the future of construction.
            </p>
            <Link to="/contact-us">
              <Button className="bg-white  hover:bg-gray-100 px-8 py-3 ">
               <span className="text-lg font-semibold text-blue-900">
                Get In Touch</span>
              </Button>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent"></div>
      </div>

      {/* Company Overview with Enhanced Design */}
      <Section
        title={aboutUsData.section1.title}
        desc={aboutUsData.section1.desc}
        className="bg-white relative overflow-hidden"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-12 items-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-400 to-blue-600 rounded-lg blur opacity-25"></div>
            <img 
              src="/images/home/c2.jpg" 
              alt="Rakan EG Company" 
              className="relative w-full rounded-lg shadow-2xl transform hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="space-y-8">
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-xl text-center">
                <h3 className="text-3xl font-bold text-blue-600 mb-3">50+</h3>
                <p className="text-gray-700">Projects Completed</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-xl text-center">
                <h3 className="text-3xl font-bold text-green-600 mb-3">15+</h3>
                <p className="text-gray-700">Years Experience</p>
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-8 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Our Commitment</h3>
              <p className="text-blue-100 leading-relaxed">
                We deliver excellence through innovation, quality, and unwavering commitment to our clients' success.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Vision & Values with Modern Cards */}
      <Section
        title={aboutUsData.section2.title}
        desc={aboutUsData.section2.desc}
        className="bg-gradient-to-br from-gray-50 to-blue-50"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
          {aboutUsData.section2.cards.map((card, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl p-10 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-t-4 border-blue-500"
            >
              <div className="text-4xl text-blue-500 mb-6 group-hover:scale-110 transition-transform duration-300">
                {card.icon}
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-800">{card.title}</h3>
              <div className="w-12 h-1 bg-gradient-to-r from-blue-400 to-blue-600 rounded mb-6"></div>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors leading-relaxed">
                Our commitment to {card.title.toLowerCase()} drives everything we do.
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* Services Overview with Hover Effects */}
      <Section
        title="Our Expertise"
        desc="We deliver comprehensive electromechanical solutions across multiple industries"
        className="bg-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-transparent to-blue-50 opacity-50"></div>
        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mt-12">
          {[
            {
              image: "/images/services/hvac_231473096-1024x683.jpg",
              title: "HVAC Systems",
              desc: "Advanced climate control solutions for optimal indoor comfort",
              link: "/HVAC",
              color: "from-blue-500 to-blue-600"
            },
            {
              image: "/images/services/electrical.jpeg",
              title: "Electrical Systems", 
              desc: "Comprehensive electrical infrastructure and installations",
              link: "/Electrical",
              color: "from-yellow-500 to-orange-500"
            },
            {
              image: "/images/services/Plumbing-624x443.png",
              title: "Plumbing Systems",
              desc: "Reliable water supply and drainage solutions",
              link: "/Plumbing",
              color: "from-cyan-500 to-blue-500"
            }
          ].map((service, index) => (
            <div key={index} className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src={service.image} 
                  alt={service.title} 
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-0 group-hover:opacity-80 transition-opacity duration-500`}></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                  <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                  <p className="text-gray-200 mb-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200 leading-relaxed">
                    {service.desc}
                  </p>
                  <Link to={service.link}>
                    <button className="bg-white text-gray-800 px-6 py-3 rounded-full font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-300 hover:bg-gray-100">
                      Learn More
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Call to Action Section */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white py-24">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl font-bold mb-8">Ready to Start Your Project?</h2>
          <p className="text-xl mb-12 text-blue-100 leading-relaxed">
            Let's discuss how Rakan EG can bring your vision to life with our expertise and dedication.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/projects">
              <Button className="bg-white  hover:bg-gray-100 px-8 py-3 ">
                <span className="text-lg font-semibold text-blue-900">
                View Our Projects</span>
              </Button>
            </Link>
            <Link to="/contact-us">
              <Button outline={true} className="border-white text-white hover:bg-white  px-8 py-3 ">
                <span className="text-lg font-semibold text-blue-900">
                Contact Us Today</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default About;
